import { Component, Fragment, ReactNode } from "react";

import { captureDatadogError } from "@/common/helpers/datadog";

interface StripeElementErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface StripeElementErrorBoundaryState {
  hasError: boolean;
  failureCount: number;
}

const MAX_REMOUNTS = 1;

export class StripeElementErrorBoundary extends Component<
  StripeElementErrorBoundaryProps,
  StripeElementErrorBoundaryState
> {
  state: StripeElementErrorBoundaryState = { hasError: false, failureCount: 0 };

  static getDerivedStateFromError(): Partial<StripeElementErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    const failureCount = this.state.failureCount + 1;
    captureDatadogError({
      message: "[StripeElementErrorBoundary] Stripe element crashed",
      error,
      extraTags: { crash_frontend: false, failure_count: failureCount, will_remount: failureCount <= MAX_REMOUNTS },
    });
    this.setState({ hasError: false, failureCount });
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    if (this.state.failureCount > MAX_REMOUNTS) {
      return this.props.fallback;
    }
    return <Fragment key={this.state.failureCount}>{this.props.children}</Fragment>;
  }
}
